/**
 * 「发布到官网」弹窗(1009 一期):选站点 → 立即发 / 定时发 → 发。
 *
 * ★ 发布前先过导出前检查(同导出同一道门):blocking 已在按钮层禁死;confirmation(没评分 / HIGH 风险 / 行业禁入)
 *   在弹窗里逐条列出,勾「我已确认」才放行(后端同口径再校一遍,未确认回 409)。
 * ★ 站点模板没有动态新闻列表时照发,但如实提示「新闻列表里看不到,只能经链接访问」。
 * ★ 发布不扣积分(生成时已扣)。
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { errorText } from "@/shared/lib/apiErrors";
import { humanizeReporterText } from "../model/labels";

import type { ExportPreflight } from "../api/reporterService";
import {
  reporterSiteService,
  type ArticlePublications,
  type SiteOption,
} from "../api/reporterSiteService";
import {
  minScheduleInput,
  SITE_OPTIONS_KEY,
  siteLabel,
  siteOptionText,
} from "../model/sitePublication";

export function SitePublishDialog({
  open,
  onOpenChange,
  articleId,
  preflight,
  defaultSiteId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  articleId: number | string;
  preflight: ExportPreflight | undefined;
  defaultSiteId?: string | null;
  onDone: (r: ArticlePublications, siteId: string) => void;
}) {
  const sitesQ = useQuery({
    queryKey: SITE_OPTIONS_KEY,
    queryFn: () => reporterSiteService.sites(),
    enabled: open,
  });
  const sites: SiteOption[] = sitesQ.data ?? [];
  const [siteId, setSiteId] = useState<string>(defaultSiteId ?? "");
  const [mode, setMode] = useState<"now" | "later">("now");
  const [at, setAt] = useState<string>("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 预选的站(建站舱带来的 ?site= / 本标签页记下的)不在可发列表里(已下线 / 换了租户)就当没选,别带着一个发不了的 id 去提交
  const effectiveSite = sites.some((s) => s.siteId === siteId)
    ? siteId
    : sites.length === 1
      ? sites[0].siteId
      : "";
  const site = sites.find((s) => s.siteId === effectiveSite) ?? null;
  const confirmations = preflight?.confirmation ?? [];
  const blocked = (preflight?.blocking.length ?? 0) > 0;
  const needConfirm = confirmations.length > 0 && !confirmed;
  const needTime = mode === "later" && !at;
  const canSubmit = !!effectiveSite && !blocked && !needConfirm && !needTime && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await reporterSiteService.publish(articleId, {
        siteId: effectiveSite,
        scheduledAt: mode === "later" ? at : null,
        confirm: confirmations.length > 0 && confirmed,
      });
      onDone(r, effectiveSite);
      onOpenChange(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-site-publish-dialog="">
        <DialogHeader>
          <DialogTitle>发布到官网</DialogTitle>
          <DialogDescription>文章发到建站舱的官网站点「新闻」栏。发布不扣积分。</DialogDescription>
        </DialogHeader>

        {sitesQ.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            正在读取站点…
          </div>
        ) : sitesQ.isError ? (
          <p className="text-sm text-destructive">{errorText(sitesQ.error)}</p>
        ) : sites.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-site-publish-empty="">
            还没有已上线的官网站点。先在建站舱建站并发布一次,再回来发文章。
          </p>
        ) : (
          <div className="space-y-4 text-sm">
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold">发到哪个站点</span>
              <select
                aria-label="选择站点"
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={effectiveSite}
                onChange={(e) => setSiteId(e.target.value)}
              >
                {sites.length > 1 && <option value="">请选择站点</option>}
                {sites.map((s) => (
                  <option key={s.siteId} value={s.siteId}>
                    {siteOptionText(s)}
                  </option>
                ))}
              </select>
            </label>
            {site && (
              <p className="text-xs text-muted-foreground" data-site-publish-target="">
                发到:{siteLabel(site.name, site.siteUrl)}
                {site.articleListSupported === true && " · 发布后自动出现在官网新闻列表"}
              </p>
            )}
            {site?.hint && (
              <p
                className="rounded-md border border-warning/50 bg-warning/5 px-2.5 py-2 text-xs text-warning"
                data-site-publish-hint=""
              >
                {site.hint}
              </p>
            )}

            <fieldset className="space-y-1.5">
              <legend className="text-xs font-semibold">什么时候发</legend>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="site-publish-mode"
                  checked={mode === "now"}
                  onChange={() => setMode("now")}
                />
                立即发布
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="site-publish-mode"
                  checked={mode === "later"}
                  onChange={() => setMode("later")}
                />
                定时发布
              </label>
              {mode === "later" && (
                <input
                  type="datetime-local"
                  aria-label="发布时间"
                  className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  min={minScheduleInput()}
                  value={at}
                  onChange={(e) => setAt(e.target.value)}
                />
              )}
            </fieldset>

            {confirmations.length > 0 && (
              <div className="rounded-md border border-warning/50 bg-warning/5 px-2.5 py-2 text-xs leading-relaxed">
                <b className="text-warning">发布前需要确认:</b>
                {confirmations.map(humanizeReporterText).join(";")}
                <label className="mt-1.5 flex items-center gap-2">
                  <input
                    type="checkbox"
                    aria-label="我已确认"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  我已确认,照发(会记录审计)
                </label>
              </div>
            )}
            {blocked && (
              <p className="text-xs text-destructive">
                不能发:{preflight!.blocking.map(humanizeReporterText).join(";")}
              </p>
            )}
            {error && (
              <p className="text-xs text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button disabled={!canSubmit} onClick={() => void submit()} data-site-publish-submit="">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === "later" ? "排期发布" : "立即发布"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
